import { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

export default function CodeEditor({
  value = "",
  onChange,
  language = "solidity",
  placeholder = "",
  readOnly = false,
  minHeight = 360,
  fillHeight = false,
  className = "",
}) {
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const lineCount = useMemo(() => {
    if (!value) return 1;
    return value.split("\n").length;
  }, [value]);

  const lineNumbers = useMemo(
    () => Array.from({ length: lineCount }, (_, i) => i + 1).join("\n"),
    [lineCount]
  );

  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleKeyDown = (e) => {
    if (readOnly) return;
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = textareaRef.current;
      if (!ta) return;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const indent = "  ";
      const next = value.substring(0, start) + indent + value.substring(end);
      onChange?.(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + indent.length;
      });
    }
  };

  const wrapperStyle = fillHeight ? undefined : { minHeight };
  const textareaStyle = fillHeight
    ? { tabSize: 2, whiteSpace: "pre" }
    : { minHeight, tabSize: 2, whiteSpace: "pre" };

  return (
    <div
      className={`flex w-full overflow-hidden rounded-lg border bg-[#1e1e2e] text-gray-100 transition-all ${
        focused ? "border-blue-500 ring-2 ring-blue-500/30" : "border-gray-700"
      } ${fillHeight ? "h-full min-h-0" : ""} ${className}`}
      style={wrapperStyle}
    >
      <pre
        ref={lineNumbersRef}
        aria-hidden="true"
        className="h-full select-none overflow-hidden border-r border-gray-700 bg-[#181826] px-3 py-3 text-right font-mono text-sm leading-[1.55] text-gray-500"
        style={{ minWidth: 48 }}
      >
        {lineNumbers}
      </pre>
      <div className="relative flex-1 h-full">
        {language && (
          <span className="pointer-events-none absolute right-3 top-2 z-10 select-none rounded bg-gray-800/80 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-300">
            {language}
          </span>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          spellCheck={false}
          readOnly={readOnly}
          placeholder={placeholder}
          className="block h-full w-full resize-none bg-transparent px-3 py-3 font-mono text-sm leading-[1.55] text-gray-100 placeholder-gray-500 outline-none"
          style={textareaStyle}
        />
      </div>
    </div>
  );
}

CodeEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  language: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  minHeight: PropTypes.number,
  fillHeight: PropTypes.bool,
  className: PropTypes.string,
};
